import type { CSSProperties } from "react";

// The single source of design values for every Maintenance Monkey UI.
// A raw colour, dimension, or font size in a component is a defect: it is the value
// that will not be found the day this object changes. Every entry carries a one line
// note saying what it is for; measured values state their measurement.
//
// Colour model: one opaque ground, and one light tone (white) laid over it at a ladder
// of alphas. The silver a viewer reads is that ladder, not a declared grey. Accents are
// ranked (red, blue, gold, green) and each carries state, never decoration. Contrast is
// measured against `ground` and recorded on the token.

// White at alpha over the ground. Produces the surface and text ladder without a second palette.
const white = (a: number): string => `rgba(255, 255, 255, ${a})`;

export const tokens = {
	color: {
		// The one opaque colour the whole app sits on. Every contrast ratio below is measured against it.
		ground: "#0B0C0E",
		// Slightly lifted ground for a bar or well that must read as set into the page.
		groundSunken: "#08090A",

		surface: {
			// Card and panel fill. Lowest step of the ladder so structure comes from the stroke, not the box.
			raised: white(0.04),
			// Hover or a nested surface that must separate from the card under it.
			raisedStrong: white(0.07),
			// Pressed or selected surface.
			active: white(0.1),
		},

		stroke: {
			// 1px structural hairline. Carries the edge of a card; the fill barely registers.
			hairline: white(0.1),
			// Emphasis edge: focus ring, active control outline.
			strong: white(0.22),
			// Faint internal divider between rows of one surface.
			divider: white(0.06),
		},

		text: {
			// Body and headings. Composited near #ECECEC over the ground: contrast 16:1.
			primary: white(0.92),
			// Secondary copy and micro cap labels. Composited near #A7A7A7: contrast 8:1.
			secondary: white(0.64),
			// Least emphasis, large text or non essential only. Composited near #777: contrast 4.4:1.
			muted: white(0.44),
			// Text placed on an accent fill; the ground shows through nothing here.
			onAccent: "#0B0C0E",
		},

		// Accent ladder, ranked. Take the highest that fits the meaning; reaching down is a decision.
		accent: {
			// Rank 1. Failure, a destructive act, the machine reflash write. #FF5C5C on ground: contrast 6.5:1.
			red: "#FF5C5C",
			// Rank 2. The primary interactive affordance, active step, informational state. #5AA9FF: contrast 8:1.
			blue: "#5AA9FF",
			// Rank 3. Pending attention, warning, degraded. #E8B84B: contrast 10.6:1.
			gold: "#E8B84B",
			// Rank 4. Success, a healthy component, a completed step. #45C08A: contrast 8.5:1.
			green: "#45C08A",
		},

		// Accent at low alpha for a tint fill behind its own hairline. Keeps an accented control an outline, not a block.
		accentWash: {
			red: "rgba(255, 92, 92, 0.12)",
			blue: "rgba(90, 169, 255, 0.12)",
			gold: "rgba(232, 184, 75, 0.12)",
			green: "rgba(69, 192, 138, 0.12)",
		},
	},

	// One spacing grid on a 4px base. Name the step and use the name.
	space: {
		xs: 4, // tight gap inside a control
		sm: 8, // gap between a label and its value
		md: 12, // gap between related rows
		lg: 16, // default padding inside a card; the minimum side gutter
		xl: 24, // gap between cards
		xxl: 32, // section separation
		xxxl: 48, // page top and bottom breathing room
	},

	// Corner radius. Generous enough that a low contrast card reads as an object, not a box.
	radius: {
		sm: 6, // input, small control
		md: 10, // button
		lg: 16, // card, panel
		pill: 999, // status pill, fully rounded
	},

	// Stroke weights in px. Structure is carried by the hairline, so its weight is a named constant.
	border: {
		hairline: 1, // structural edge and internal divider
		focus: 2, // focus ring, thicker so it reads over the hairline
	},

	// Small glyph diameters that are not layout spacing: a status dot, a determinate track.
	glyph: {
		dot: 8, // status and brand dot
		track: 4, // determinate progress track height
	},

	font: {
		// Content voice. System stack so no font file ships or blocks first paint.
		sans: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
		// Numeric readout voice. Monospaced digits so a live value does not jitter as digits change.
		mono: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
	},

	// Type sizes in px. Two voices only: content set normally, labels and measures set as micro caps.
	size: {
		micro: 11, // micro cap label
		body: 14, // body text
		bodyLg: 16, // emphasised body, input text
		title: 20, // card and section title
		display: 34, // stat tile value
	},

	weight: {
		regular: 400,
		medium: 500,
		semibold: 600,
	},

	line: {
		tight: 1.15, // display and titles
		body: 1.5, // running text
	},

	track: {
		// Wide tracking for uppercase micro caps so a label stops competing with the value it names.
		caps: "0.09em",
		normal: "0em",
	},

	// Composed type styles. Uppercasing is applied by the component (CSS transform), so the source string stays readable.
	type: {
		// Micro cap label: wide tracked, small, secondary weight. Component adds textTransform.
		microcap: {
			fontSize: 11,
			fontWeight: 600,
			letterSpacing: "0.09em",
			textTransform: "uppercase",
			lineHeight: 1.15,
		} as CSSProperties,
		// Numeric readout: tabular figures so columns of numbers align and a changing digit holds its place.
		numeric: {
			fontVariantNumeric: "tabular-nums",
			fontFeatureSettings: '"tnum" 1',
		} as CSSProperties,
	},

	// Motion is fast and sits between the viewer and something they want to see.
	motion: {
		state: 140, // ms: a state change inside a screen (hover, press, toggle)
		entrance: 220, // ms: content entering or leaving
		stagger: 40, // ms: step between staggered entrance items
		staggerBudget: 360, // ms: cap on total stagger so the sequence flattens before it reaches the controls
		breath: 2600, // ms: slow breath of the one active mark; says work is happening now
		rise: 8, // px: distance content rises into place on entrance
		easing: "cubic-bezier(0.2, 0, 0, 1)", // standard ease for state and entrance
	},

	// Layout limits. A row stretched across a wide viewport is not a layout.
	layout: {
		contentMax: 1040, // px: cap on content width, then centre
		formMax: 400, // px: cap on a single column form
		gutter: 16, // px: minimum side gutter at every width
		tileMin: 200, // px: minimum stat tile width before the grid collapses to one column
	},

	// Minimum touch target on anything tappable, whatever glyph sits inside it.
	touch: {
		min: 48, // px
	},

	// Stacking order for the few layered surfaces.
	z: {
		base: 0,
		header: 10,
		overlay: 100,
	},
} as const;

// Entrance delay for the item at `index`, capped by the stagger budget so late items do not queue.
export function enterDelay(index: number): string {
	const raw = index * tokens.motion.stagger;
	const capped = Math.min(raw, tokens.motion.staggerBudget - tokens.motion.entrance);
	return `${Math.max(0, capped)}ms`;
}

// Standard transition for a state change on one property (default: all).
export function stateTransition(property = "all"): string {
	return `${property} ${tokens.motion.state}ms ${tokens.motion.easing}`;
}

// The hairline stroke recipe: the named weight in the given colour. Structure comes from this edge,
// so every card, row, and input reaches it here rather than restating the width.
export function hairline(color: string): string {
	return `${tokens.border.hairline}px solid ${color}`;
}

// The four accent names, ranked. A control names the state it carries; this maps it to a colour once.
export type AccentName = keyof typeof tokens.color.accent;

export type Tokens = typeof tokens;
